import { useCallback, useMemo, useRef } from 'react';
import { setError } from 'store/aiScribe/aiScribeSlice';
import { uploadRecordingChunk } from 'store/aiScribe/aiScribeActions';
import { useAppDispatch } from 'store/hooks';

import { RecordedChunk } from './types';

type UseAiScribeChunkUploaderOptions = {
  clientId: number | null;
};

type UploadChunkOptions = {
  recordingSessionId: string | null;
  chunk: RecordedChunk;
};

export const useAiScribeChunkUploader = ({ clientId }: UseAiScribeChunkUploaderOptions) => {
  const dispatch = useAppDispatch();
  const nextOrdinalRef = useRef(0);
  const pendingUploadsRef = useRef<Set<Promise<void>>>(new Set());
  const hasUploadFailureRef = useRef(false);

  const uploadChunk = useCallback(
    ({ recordingSessionId, chunk }: UploadChunkOptions) => {
      if (!recordingSessionId) {
        hasUploadFailureRef.current = true;
        dispatch(setError('System error: Consider taking notes.'));
        return Promise.resolve();
      }

      nextOrdinalRef.current += 1;
      const ordinalNumber = nextOrdinalRef.current;

      const uploadPromise = dispatch(
        uploadRecordingChunk({
          clientId,
          recordingSessionId,
          ordinalNumber,
          chunk: chunk.blob,
          duration: chunk.durationSeconds
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.status === 'ERROR') {
            hasUploadFailureRef.current = true;
          }
        })
        .catch((error) => {
          hasUploadFailureRef.current = true;

          console.warn('AI Scribe chunk upload failed after retries', {
            recordingSessionId,
            ordinalNumber,
            error
          });
        })
        .finally(() => {
          pendingUploadsRef.current.delete(uploadPromise);
        });

      pendingUploadsRef.current.add(uploadPromise);

      return uploadPromise;
    },
    [clientId, dispatch]
  );

  const waitForPendingUploads = useCallback(async () => {
    await Promise.allSettled([...pendingUploadsRef.current]);
  }, []);

  const reset = useCallback((lastOrdinalNumber = 0) => {
    nextOrdinalRef.current = lastOrdinalNumber;
    pendingUploadsRef.current.clear();
    hasUploadFailureRef.current = false;
  }, []);

  const getTotalOrdinals = useCallback(() => nextOrdinalRef.current, []);
  const hasUploadFailure = useCallback(() => hasUploadFailureRef.current, []);

  return useMemo(
    () => ({
      uploadChunk,
      waitForPendingUploads,
      reset,
      getTotalOrdinals,
      hasUploadFailure
    }),
    [getTotalOrdinals, hasUploadFailure, reset, uploadChunk, waitForPendingUploads]
  );
};
